import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { graphql } from 'src/gql'
import { EngagementInput } from 'src/gql/graphql'

export const EngagementFragment = graphql(`
  fragment Engagement on Engagement {
    id
    successful
    startedAt
    type
    duration
    notes
    planChanges
    goalProgress
    referrals
    actions
    createdAt
    updatedAt
  }
`)

const Create = graphql(`
  mutation CreateEngagement($input: EngagementInput!) {
    createEngagement(input: $input) {
      success
      message
      engagement {
        ... on Engagement {
          ...Engagement
          peer {
            id
            name
          }
        }
      }
    }
  }
`)

const Update = graphql(`
  mutation UpdateEngagement($id: Int!, $input: EngagementInput!) {
    updateEngagement(id: $id, input: $input) {
      success
      message
      engagement {
        ... on Engagement {
          ...Engagement
          peer {
            id
            name
          }
        }
      }
    }
  }
`)

const Delete = graphql(`
  mutation DeleteEngagement($id: Int!) {
    deleteEngagement(id: $id) {
      success
      message
      engagement {
        ... on Engagement {
          id
        }
      }
    }
  }
`)

export const useEngagements = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [create] = useMutation(Create, {
    onCompleted: ({ createEngagement: { success, message } }) => {
      enqueueSnackbar(message, { variant: success ? 'success' : 'error' })
    },
    onError: () => {
      enqueueSnackbar('Oops. Something went wrong.', { variant: 'error' })
    },
    refetchQueries: ['QueryEngagements', 'PeerOneOnOneSessions'],
  })
  const [update] = useMutation(Update, {
    onCompleted: ({ updateEngagement: { success, message } }) => {
      enqueueSnackbar(message, { variant: success ? 'success' : 'error' })
    },
    onError: () => {
      enqueueSnackbar('Oops. Something went wrong.', { variant: 'error' })
    },
  })
  const [remove] = useMutation(Delete, {
    onCompleted: ({ deleteEngagement: { success, message } }) => {
      enqueueSnackbar(message, { variant: success ? 'success' : 'error' })
    },
    onError: () =>
      enqueueSnackbar('Oops. Something went wrong.', { variant: 'error' }),
    update(cache, { data }) {
      if (data?.deleteEngagement.engagement?.__typename !== 'Engagement') return
      const normalizedId = cache.identify({
        id: data?.deleteEngagement.engagement.id,
        __typename: data?.deleteEngagement.engagement?.__typename,
      })
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

  return {
    create: async (input: EngagementInput) => create({ variables: { input } }),
    update: async (id: number, input: EngagementInput) =>
      update({ variables: { id, input } }),
    remove: async (id: number) => remove({ variables: { id } }),
  }
}
